/* ============================================================
   TASK-313 — ?plan=<key> intent: carries a landing CTA's intended plan
   (posting/starter/growth/pro/agency) through signup/login and the OAuth /
   email-verify round trips (full page reloads, sometimes a brand-new tab —
   an email-verify link opens one), so the post-signup picker
   (PlanSelectScreen.jsx) and /app/plans (Plans.jsx) can preselect/highlight
   it.

   Mirrors lib/signupSource.js's shape exactly: localStorage (not
   sessionStorage — a new-tab email-verify link would lose a session-scoped
   value), get/set/clear each wrapped in try/catch, fail-silent like every
   other attribution helper in this codebase (funnelSource.js,
   signupSource.js). Pure module — no React, no fetch — testable under plain
   vitest (see planIntent.test.js).
   ============================================================ */

export const PLAN_INTENT_KEY = 'dathent.plan.intent';

// Mirrors lib/postingPlan.js's POSTING_PLAN_KEY ('posting') plus
// lib/plans.js's NEW_PLAN_DEFS ids (starter/growth/pro/agency). Kept as its
// own literal list rather than imported so this module stays
// framework/network-free (lib/plans.js pulls in the API client at module
// scope) — update both lists together if a plan id is ever added/removed.
export const VALID_PLAN_KEYS = ['posting', 'starter', 'growth', 'pro', 'agency'];

function isValidPlanKey(key) {
  return typeof key === 'string' && VALID_PLAN_KEYS.includes(key);
}

/**
 * Reads `plan` off a query string and persists it — but only when it is one
 * of the known keys. An absent or unknown value is a no-op: it must NOT
 * clear an intent a previous page in the same flow already captured (e.g.
 * /signup?plan=growth -> the post-verify landing has no `plan` param at
 * all, and must not erase what /signup stored).
 */
export function capturePlanIntent(search, storage = globalThis?.localStorage) {
  try {
    const key = new URLSearchParams(search || '').get('plan');
    if (isValidPlanKey(key)) storage?.setItem(PLAN_INTENT_KEY, key);
  } catch {
    // Storage can be unavailable in private browsing; the intent is simply not carried.
  }
}

/** Returns the persisted plan intent, or null if absent/unknown/unreadable. */
export function getPlanIntent(storage = globalThis?.localStorage) {
  try {
    const key = storage?.getItem(PLAN_INTENT_KEY);
    return isValidPlanKey(key) ? key : null;
  } catch {
    return null;
  }
}

/** Clears the persisted plan intent — never throws. */
export function clearPlanIntent(storage = globalThis?.localStorage) {
  try {
    storage?.removeItem(PLAN_INTENT_KEY);
  } catch {
    // Storage can be unavailable in private browsing; nothing to clear anyway.
  }
}

/**
 * Pure decision: which plan a picker should preselect.
 *
 * The existing ChatGPT-origin rule (signupSource.js's isChatGptOrigin) takes
 * PRECEDENCE over the URL intent, per the issue spec — every caller already
 * gates that rule on the Scheduler track being active (PlanSelectScreen.jsx,
 * TrialCardCaptureContainer.jsx: `if (!postingPlanActive) return;` before
 * setting 'posting'), so postingPlanActive is required here too, on both
 * sides, before 'posting' can win. An intent of 'posting' while the
 * Scheduler track is inactive, or any other unknown/absent intent, falls
 * through to the caller's own default — today's behaviour, byte-identical.
 */
export function resolvePreselectedPlan({
  intentPlanKey = null,
  chatGptOrigin = false,
  postingPlanActive = false,
  fallback = 'growth',
} = {}) {
  if (chatGptOrigin && postingPlanActive) return 'posting';
  if (intentPlanKey === 'posting') return postingPlanActive ? 'posting' : fallback;
  if (isValidPlanKey(intentPlanKey)) return intentPlanKey;
  return fallback;
}
